import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { invoiceService } from '@/services/invoiceService';
import { Invoice, MabarTransaction } from '@/types/invoice';

// Helper for formatting date to Month Year (e.g. AGUSTUS 2026)
const getMonthYearKey = (dateStr?: string): string => {
  if (!dateStr) return 'LAINNYA';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'LAINNYA';
    const realMonths = [
      'JANUARI',
      'FEBRUARI',
      'MARET',
      'APRIL',
      'MEI',
      'JUNI',
      'JULI',
      'AGUSTUS',
      'SEPTEMBER',
      'OKTOBER',
      'NOVEMBER',
      'DESEMBER',
    ];
    return `${realMonths[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return 'LAINNYA';
  }
};

// Helper for formatting item date (e.g. 3 Agu 2026)
const formatItemDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return dateStr || '';
  }
};

// Helper for formatting currency to Rp. XXX.XXX
const formatRupiah = (amount?: number): string => {
  if (!amount && amount !== 0) return 'Rp. 0';
  return `Rp. ${amount.toLocaleString('id-ID')}`;
};

// Helper for Status Badge Pill
const renderStatusBadge = (status?: string) => {
  const s = (status || '').toUpperCase();
  if (s === 'PAID' || s === 'LUNAS' || s === 'SUCCESS' || s === 'SETTLED') {
    return (
      <span className="bg-emerald-50 text-emerald-600 border border-emerald-100 font-semibold text-[10px] px-2.5 py-0.5 rounded-full inline-block mt-1.5 tracking-wide">
        PAID
      </span>
    );
  }
  if (s === 'PENDING' || s === 'WAITING' || s === 'MENUNGGU PEMBAYARAN') {
    return (
      <span className="bg-amber-50 text-amber-600 border border-amber-100 font-semibold text-[10px] px-2.5 py-0.5 rounded-full inline-block mt-1.5 tracking-wide">
        PENDING
      </span>
    );
  }
  if (s === 'UNPAID' || s === 'BELUM BAYAR') {
    return (
      <span className="bg-rose-50 text-rose-500 border border-rose-100 font-semibold text-[10px] px-2.5 py-0.5 rounded-full inline-block mt-1.5 tracking-wide">
        UNPAID
      </span>
    );
  }
  if (s === 'REFUND' || s === 'REFUNDED') {
    return (
      <span className="bg-blue-50 text-blue-600 border border-blue-100 font-semibold text-[10px] px-2.5 py-0.5 rounded-full inline-block mt-1.5 tracking-wide">
        REFUND
      </span>
    );
  }
  return (
    <span className="bg-gray-100 text-gray-500 border border-gray-200 font-semibold text-[10px] px-2.5 py-0.5 rounded-full inline-block mt-1.5 tracking-wide">
      {s || 'EXPIRED'}
    </span>
  );
};

// Tab button component matching ClientMabarPage style
const TabButton = ({
  tab,
  activeTab,
  icon,
  label,
  onClick,
}: {
  tab: 'booking' | 'event';
  activeTab: 'booking' | 'event';
  icon: string;
  label: string;
  onClick: (tab: 'booking' | 'event') => void;
}) => (
  <button
    type="button"
    onClick={() => onClick(tab)}
    className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-all cursor-pointer ${
      activeTab === tab
        ? 'border-[#FC480D] text-[#FC480D]'
        : 'border-transparent text-gray-400 hover:text-[#0E1726]'
    }`}
  >
    <Icon icon={icon} className="w-4 h-4" />
    <span>{label}</span>
  </button>
);

// Skeleton loading for transaction cards list
const TransactionListSkeleton: React.FC = () => (
  <div className="space-y-6 pt-1">
    <div>
      {/* Month Year Header Skeleton */}
      <div className="h-3.5 w-28 bg-gray-200 rounded-md animate-pulse mb-3 px-1"></div>

      {/* Cards Skeletons (5 items matching page limit) */}
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, idx) => (
          <div
            key={idx}
            className="bg-white rounded-2xl p-4 border border-[#E0E6ED] shadow-2xs flex items-center justify-between gap-3"
          >
            {/* Left Info */}
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded-md w-3/4 animate-pulse"></div>
              <div className="h-3 bg-gray-100 rounded-md w-1/2 animate-pulse"></div>
              <div className="h-4 bg-gray-100 rounded-full w-16 animate-pulse"></div>
            </div>

            {/* Right Amount */}
            <div className="shrink-0">
              <div className="h-5 bg-gray-200 rounded-md w-20 animate-pulse"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const TransactionProductClient: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'booking' | 'event'>('booking');

  // Booking Data State
  const [bookingPage, setBookingPage] = useState(1);
  const [bookingTotalPages, setBookingTotalPages] = useState(1);
  const [bookingTotal, setBookingTotal] = useState(0);
  const [loadingBooking, setLoadingBooking] = useState(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  // Event Data State
  const [eventPage, setEventPage] = useState(1);
  const [eventTotalPages, setEventTotalPages] = useState(1);
  const [eventTotal, setEventTotal] = useState(0);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [mabarTransactions, setMabarTransactions] = useState<MabarTransaction[]>([]);

  // Fetch Booking Invoices
  const fetchBookingInvoices = async (page: number = 1) => {
    try {
      setLoadingBooking(true);
      const res = await invoiceService.getInvoicesByCustomer(page, 5, 'date', 'desc');
      if (res.success && res.data) {
        setInvoices(res.data);
        if (res.pagination) {
          setBookingTotalPages(res.pagination.total_pages || 1);
          setBookingTotal(res.pagination.total || res.data.length);
        }
      } else {
        setInvoices([]);
        setBookingTotalPages(1);
        setBookingTotal(0);
      }
    } catch (err) {
      console.error('Failed to load booking invoices:', err);
      setInvoices([]);
      setBookingTotalPages(1);
      setBookingTotal(0);
    } finally {
      setLoadingBooking(false);
    }
  };

  // Fetch Event Mabar Transactions
  const fetchEventTransactions = async (page: number = 1) => {
    try {
      setLoadingEvent(true);
      const res = await invoiceService.getClientMabarTransactions(page, 5, '', 'created_at', 'desc');
      if (res.success && res.data) {
        setMabarTransactions(res.data);
        if (res.pagination) {
          setEventTotalPages(res.pagination.total_pages || 1);
          setEventTotal(res.pagination.total || res.data.length);
        }
      } else {
        setMabarTransactions([]);
        setEventTotalPages(1);
        setEventTotal(0);
      }
    } catch (err) {
      console.error('Failed to load event transactions:', err);
      setMabarTransactions([]);
      setEventTotalPages(1);
      setEventTotal(0);
    } finally {
      setLoadingEvent(false);
    }
  };

  useEffect(() => {
    fetchBookingInvoices(bookingPage);
  }, [bookingPage]);

  useEffect(() => {
    fetchEventTransactions(eventPage);
  }, [eventPage]);

  // Group Booking Invoices by Month Year
  const groupInvoicesByMonth = () => {
    if (!invoices || invoices.length === 0) return [];

    const groupMap: Record<string, any[]> = {};
    invoices.forEach((inv) => {
      const key = getMonthYearKey(inv.transaction_date);
      if (!groupMap[key]) groupMap[key] = [];
      groupMap[key].push({
        id: inv.invoice_id || inv.invoice_number,
        invoice_number: inv.invoice_number,
        title: `${inv.arena_name || 'Arena'}${inv.product ? ` · ${inv.product}` : ''}`,
        dateStr: formatItemDate(inv.transaction_date),
        paymentMethod: inv.payment_method || 'Pembayaran',
        status: inv.payment_status,
        amount: inv.total_amount,
      });
    });

    return Object.keys(groupMap).map((monthYear) => ({
      monthYear,
      items: groupMap[monthYear],
    }));
  };

  // Group Event Transactions by Month Year
  const groupEventTransactionsByMonth = () => {
    if (!mabarTransactions || mabarTransactions.length === 0) return [];

    const groupMap: Record<string, any[]> = {};
    mabarTransactions.forEach((tx) => {
      const key = getMonthYearKey(tx.created_at);
      if (!groupMap[key]) groupMap[key] = [];
      groupMap[key].push({
        id: tx.id || tx.order_number,
        orderNumber: tx.order_number,
        title: tx.event_title || 'Event Mabar',
        dateStr: formatItemDate(tx.created_at),
        paymentMethod: tx.payment_method || 'Pembayaran',
        status: tx.status,
        amount: tx.amount,
        xendit_url: tx.xendit_invoice_url,
        event_slug: tx.event_slug,
        rawObj: tx,
      });
    });

    return Object.keys(groupMap).map((monthYear) => ({
      monthYear,
      items: groupMap[monthYear],
    }));
  };

  const bookingGroups = groupInvoicesByMonth();
  const eventGroups = groupEventTransactionsByMonth();

  const handleBookingItemClick = (invoiceNumber: string) => {
    navigate(`/transactions/booking/${invoiceNumber}`);
  };

  const handleEventItemClick = (item: any) => {
    if (item.orderNumber) {
      navigate(`/transactions/event/${item.orderNumber}`);
    }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] pb-6">
      {/* Sticky Tab Navigation (Matching ClientMabarPage style) */}
      <div className="sticky top-0 z-20 bg-white border-b border-[#E0E6ED] shadow-xs">
        <div className="flex items-center border-t border-[#E0E6ED]/60 px-2 max-w-2xl mx-auto">
          <TabButton
            tab="booking"
            activeTab={activeTab}
            onClick={setActiveTab}
            icon="ph:calendar-check"
            label="Booking Lapangan"
          />
          <TabButton
            tab="event"
            activeTab={activeTab}
            onClick={setActiveTab}
            icon="ph:users-three"
            label="Event"
          />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-4">
        {/* Tab 1: Booking Lapangan */}
        {activeTab === 'booking' && (
          <div>
            {loadingBooking ? (
              <TransactionListSkeleton />
            ) : bookingGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Icon icon="ph:receipt" className="w-14 h-14 text-gray-300 mb-3" />
                <p className="text-sm font-semibold text-[#0E1726]">Belum Ada Transaksi Booking</p>
                <p className="text-xs text-gray-400 mt-1 max-w-xs">
                  Riwayat pemesanan lapangan kamu akan muncul di sini.
                </p>
              </div>
            ) : (
              <>
                {bookingGroups.map((group) => (
                  <div key={group.monthYear} className="mb-6">
                    {/* Month Year Header */}
                    <h3 className="text-xs font-semibold text-gray-400 tracking-wider uppercase mb-3 px-1">
                      {group.monthYear}
                    </h3>

                    {/* Group Items */}
                    <div className="space-y-3">
                      {group.items.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => handleBookingItemClick(item.invoice_number)}
                          className="bg-white rounded-2xl p-4 border border-[#E0E6ED] shadow-2xs hover:shadow-md transition-all duration-200 cursor-pointer active:scale-[0.99] flex items-center justify-between gap-3"
                        >
                          {/* Left Info */}
                          <div className="min-w-0 flex-1">
                            <h4 className="text-sm font-semibold text-[#0E1726] truncate leading-tight">
                              {item.title}
                            </h4>
                            <p className="text-xs text-gray-400 font-medium mt-1">
                              {item.dateStr} · {item.paymentMethod}
                            </p>
                            {renderStatusBadge(item.status)}
                          </div>

                          {/* Right Amount */}
                          <div className="text-right shrink-0">
                            <span className="text-sm font-semibold text-[#0E1726]">
                              {formatRupiah(item.amount)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Booking Pagination */}
                {bookingTotalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-6 border-t border-[#E0E6ED] mt-6 px-1">
                    <span className="text-xs font-medium text-gray-500">
                      Halaman {bookingPage} dari {bookingTotalPages} ({bookingTotal} transaksi)
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setBookingPage((prev) => Math.max(1, prev - 1))}
                        disabled={bookingPage === 1 || loadingBooking}
                        className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white flex items-center justify-center text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-2xs"
                      >
                        <Icon icon="ph:caret-left-bold" className="w-4 h-4" />
                      </button>
                      {Array.from({ length: bookingTotalPages }, (_, i) => i + 1)
                        .filter((p) => Math.abs(p - bookingPage) <= 2 || p === 1 || p === bookingTotalPages)
                        .map((p, idx, arr) => {
                          const prevPage = arr[idx - 1];
                          const showEllipsis = prevPage && p - prevPage > 1;
                          return (
                            <React.Fragment key={p}>
                              {showEllipsis && <span className="text-xs text-gray-400 px-1">...</span>}
                              <button
                                type="button"
                                onClick={() => setBookingPage(p)}
                                disabled={loadingBooking}
                                className={`w-8 h-8 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                                  p === bookingPage
                                    ? 'bg-[#FC480D] text-white shadow-xs'
                                    : 'bg-white border border-[#E0E6ED] text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                {p}
                              </button>
                            </React.Fragment>
                          );
                        })}
                      <button
                        type="button"
                        onClick={() => setBookingPage((prev) => Math.min(bookingTotalPages, prev + 1))}
                        disabled={bookingPage === bookingTotalPages || loadingBooking}
                        className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white flex items-center justify-center text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-2xs"
                      >
                        <Icon icon="ph:caret-right-bold" className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Tab 2: Event Mabar */}
        {activeTab === 'event' && (
          <div>
            {loadingEvent ? (
              <TransactionListSkeleton />
            ) : eventGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Icon icon="ph:ticket" className="w-14 h-14 text-gray-300 mb-3" />
                <p className="text-sm font-semibold text-[#0E1726]">Belum Ada Transaksi Event</p>
                <p className="text-xs text-gray-400 mt-1 max-w-xs">
                  Riwayat pendaftaran event & mabar kamu akan muncul di sini.
                </p>
              </div>
            ) : (
              <>
                {eventGroups.map((group) => (
                  <div key={group.monthYear} className="mb-6">
                    {/* Month Year Header */}
                    <h3 className="text-xs font-semibold text-gray-400 tracking-wider uppercase mb-3 px-1">
                      {group.monthYear}
                    </h3>

                    {/* Group Items */}
                    <div className="space-y-3">
                      {group.items.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => handleEventItemClick(item)}
                          className="bg-white rounded-2xl p-4 border border-[#E0E6ED] shadow-2xs hover:shadow-md transition-all duration-200 cursor-pointer active:scale-[0.99] flex items-center justify-between gap-3"
                        >
                          {/* Left Info */}
                          <div className="min-w-0 flex-1">
                            <h4 className="text-sm font-semibold text-[#0E1726] truncate leading-tight">
                              {item.title}
                            </h4>
                            <p className="text-xs text-gray-400 font-medium mt-1">
                              {item.dateStr} · {item.paymentMethod}
                            </p>
                            {renderStatusBadge(item.status)}
                          </div>

                          {/* Right Amount */}
                          <div className="text-right shrink-0">
                            <span className="text-sm font-semibold text-[#0E1726]">
                              {formatRupiah(item.amount)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Event Pagination */}
                {eventTotalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-6 border-t border-[#E0E6ED] mt-6 px-1">
                    <span className="text-xs font-medium text-gray-500">
                      Halaman {eventPage} dari {eventTotalPages} ({eventTotal} transaksi)
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setEventPage((prev) => Math.max(1, prev - 1))}
                        disabled={eventPage === 1 || loadingEvent}
                        className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white flex items-center justify-center text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-2xs"
                      >
                        <Icon icon="ph:caret-left-bold" className="w-4 h-4" />
                      </button>
                      {Array.from({ length: eventTotalPages }, (_, i) => i + 1)
                        .filter((p) => Math.abs(p - eventPage) <= 2 || p === 1 || p === eventTotalPages)
                        .map((p, idx, arr) => {
                          const prevPage = arr[idx - 1];
                          const showEllipsis = prevPage && p - prevPage > 1;
                          return (
                            <React.Fragment key={p}>
                              {showEllipsis && <span className="text-xs text-gray-400 px-1">...</span>}
                              <button
                                type="button"
                                onClick={() => setEventPage(p)}
                                disabled={loadingEvent}
                                className={`w-8 h-8 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                                  p === eventPage
                                    ? 'bg-[#FC480D] text-white shadow-xs'
                                    : 'bg-white border border-[#E0E6ED] text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                {p}
                              </button>
                            </React.Fragment>
                          );
                        })}
                      <button
                        type="button"
                        onClick={() => setEventPage((prev) => Math.min(eventTotalPages, prev + 1))}
                        disabled={eventPage === eventTotalPages || loadingEvent}
                        className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white flex items-center justify-center text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-2xs"
                      >
                        <Icon icon="ph:caret-right-bold" className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionProductClient;
